import { gsap } from 'gsap';
import { Container, Ticker } from '../../odie';
import { app } from '../app';
import { PositionType } from '../../lib/defs/types';
import { IAnimation } from '../../lib/pattern/IAnimation';
import { tweenPower2In } from '../../lib/util/tweens';
import {
  BasicBlockProps,
  blockColorMap,
  ColorId,
  colorIds,
} from '../defs/block';
import { BlockEntity } from '../entities/BlockEntity';
import { mapToViewPosition } from '../util/mapTools';
import { CubeBeamAnimation } from './CubeBeamAnimation';
import { getMatch2AssetPath } from '../util/assets';
import { SuperCubeOverlayAnimation } from './SuperCubeOverlayAnimation';
import { waitFor } from '../../lib/util/waitFor';

export type SuperCubeSingleAnimationOptions = {
  color: ColorId;
  position: PositionType;
  targets: BlockEntity[];
};

type BeamEntry = {
  beam: CubeBeamAnimation;
  target: BlockEntity;
  color: ColorId;
};

const manifest = {
  super: 'power/block.cube.super.png',
  blueSparkle: 'assets/particles/sparkle_blue.png',
  greenSparkle: 'assets/particles/sparkle_green.png',
  purpleSparkle: 'assets/particles/sparkle_purple.png',
  redSparkle: 'assets/particles/sparkle_red.png',
  yellowSparkle: 'assets/particles/sparkle_yellow.png',
  sparkles: 'fx.cube/fx.cube.sparkles.png',
  wave: 'fx.bomb/fx.bomb.wave.png',
  shine: 'fx.cube/fx.cube.shine.png',
};

export class SuperCubeSingleAnimation extends Container implements IAnimation {
  private options: SuperCubeSingleAnimationOptions;
  private beams: BeamEntry[] = [];
  private completedPromise!: Promise<void>;
  private cube?: SuperCubeOverlayAnimation;

  public onBeamReachTarget?: (target: BlockEntity) => void;

  public get completed() {
    return this.completedPromise;
  }

  constructor(options: SuperCubeSingleAnimationOptions) {
    super();
    this.options = options;
  }

  public static assets(): string[] {
    return [...CubeBeamAnimation.assets(), ...Object.values(manifest)];
  }

  public async start(): Promise<void> {
    Ticker.system.add(this.update);
    app.sound.play('win');
    app.sound.play('puzzle-win-confetti');
    app.sound.play('puzzle-cube-activate');
    await this.playCube();
  }

  public stop() {
    Ticker.system.remove(this.update);
    this.stopCube();
    this.clearBeams();
  }

  private update = (dt: number) => {
    this.cube?.update(dt);
    for (const entry of this.beams) {
      // if target despawned, stop and despawn early
      if (!entry.target.scene) {
        entry.beam.stop();
        this.removeChild(entry.beam);
      }
    }
  };

  private async playCube() {
    if (this.cube) return;
    this.cube = new SuperCubeOverlayAnimation();
    this.cube.zIndex = 2;
    this.cube.speed = 0.75;
    this.addChild(this.cube);
    gsap.to(this.cube, {
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 0.5,
      ease: 'back.out',
    });
    this.cube.start();
  }

  private stopCube() {
    if (!this.cube) return;
    this.removeChild(this.cube);
    gsap.killTweensOf(this.cube);
    this.cube.stop();
    this.cube = undefined;
  }

  public async fireBeamsAtTargets(targets: BlockEntity[]) {
    if (!targets.length) return;
    app.sound.play('puzzle-cube-spin', { volume: 0.5 });
    const { position } = this.options;
    const from = mapToViewPosition(position, true);
    const promises: Promise<void>[] = [];
    const duration = 500;
    const interval = duration / targets.length;

    for (const target of targets) {
      const targetProps = target.c.block.props as BasicBlockProps;
      const targetColor = targetProps?.color ?? 'all';
      const beamColor = blockColorMap[targetColor].base;
      const to = mapToViewPosition(target.c.position.mapPosition, true);
      const direction = to.sub(from);
      const length = direction.length();

      const beam = new CubeBeamAnimation({
        color: beamColor,
        length,
        fromGlow: false,
        linearAdd: true,
      });
      beam.zIndex = length * 0.001;
      beam.rotation = -direction.angle();
      this.addChild(beam);
      this.beams.push({ beam, target, color: targetColor });

      // Store the promise of the beam animation
      const animPromise = beam
        .start({
          duration: 0.4,
          ease: 'linear',
          thickness: 1.5,
        })
        .then(async () => {
          // Short delay after reaching the target, so we can notice it better
          await waitFor(400);
          this.onBeamReachTarget?.(target);
        });

      // These are the actual promises that will be awaited at the end
      promises.push(animPromise);

      // Interval between beams
      await waitFor(interval);
    }

    // Await all beam animations completed
    await Promise.all(promises);
    app.sound.play('puzzle-cube-clear', { dupes: 1 });
  }

  public clearBeams() {
    for (const entry of this.beams) {
      entry.beam.stop();
      this.removeChild(entry.beam);
    }
    this.beams = [];
  }

  public async explode() {
    if (this.cube) {
      await gsap.to(this.cube, {
        scaleX: 1,
        scaleY: 1,
        duration: 0.3,
        ease: 'back.in',
      });
      this.stopCube();
    }

    app.sound.play('puzzle-cube-dismiss', { volume: 0.25 });

    const textures = [
      manifest.blueSparkle,
      manifest.greenSparkle,
      manifest.purpleSparkle,
      manifest.redSparkle,
      manifest.yellowSparkle,
    ].map((path) => getMatch2AssetPath(path));

    // determine scale
    const scale = 1.5;

    // determine magnitude
    const magnitudeLow = 400;
    const magnitudeHigh = 600;

    // determine duration
    const duration = 1;

    const rate = 0.005;
    const limit = 30;
    const emitterDuration = 0.25;

    // create emitter
    const emitter = app.particles.create({
      textures,
      rate,
      limit,
      emitterDuration,
      scale: [
        { x: 0.5, y: 0.5 },
        { x: scale, y: scale },
      ],
      duration,
      behaviors: [
        { type: 'explode', magnitude: [magnitudeLow, magnitudeHigh] },
        { type: 'kinematic' },
        { type: 'drag', drag: 2 },
        { type: 'fade', to: 0, tween: tweenPower2In },
      ],
    });

    this.addChild(emitter.view);
    await emitter.start();
    this.removeChild(emitter.view);
  }
}
